import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputNumber } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelect } from 'primeng/multiselect';
import { Select } from 'primeng/select';
import Detail from '../../../../../../types/detail/Detail';
import Language from '../../../../../../types/language/Language';
import Manufacturer from '../../../../../../types/manufacturer/Manufacturer';
import Media from '../../../../../../types/media/Media';
import SampleCategory from '../../../../../../types/sample-category/SampleCategory';
import SampleLanguageWriteDTO from '../../../../../../types/sample-language/SampleLanguageWriteDTO';
import SampleType from '../../../../../../types/sample-type/SampleType';
import SampleBranding from '../../../../../../types/sample-vehicle-model/SampleBranding';
import { FieldOfUse } from '../../../../../../types/sample/FieldOfUse';
import SampleFormat from '../../../../../../types/sample/SampleFormat';
import { SampleState } from '../../../../../../types/sample/SampleState';
import SampleWriteDTO from '../../../../../../types/sample/SampleWriteDTO';
import { Role } from '../../../../../../types/shared/Role';
import SelectOption from '../../../../../../types/shared/SelectOption';
import { AuthService } from '../../../../services/auth.service';
import { DetailService } from '../../../../services/detail.service';
import { LanguageService } from '../../../../services/language.service';
import { ManufacturerService } from '../../../../services/manufacturer.service';
import { MediaService } from '../../../../services/media.service';
import { SampleCategoryService } from '../../../../services/sample-category.service';
import { SampleTypeService } from '../../../../services/sample-type.service';
import { SampleService } from '../../../../services/sample.service';
import { UtilsService } from '../../../../services/utils.service';
import {
    SelectOrUploadMediaComponent
} from '../../media/select-or-upload-media-dialog/select-or-upload-media.component';

@Component({
    selector: 'app-update-sample-dialog',
    templateUrl: './update-sample-dialog.component.html',
    styleUrls: [ './update-sample-dialog.component.scss' ],
    imports: [
        DialogModule,
        InputTextModule,
        ButtonModule,
        FormsModule,
        IconField,
        InputIcon,
        IftaLabelModule,
        Select,
        InputNumber,
        MultiSelect,
        SelectOrUploadMediaComponent
    ]
})
export class UpdateSampleDialogComponent implements OnInit, OnChanges {
    @Input() visible: boolean = false;
    @Input() sample: SampleWriteDTO = new SampleWriteDTO({});
    @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() sampleUpdated: EventEmitter<any> = new EventEmitter<any>();

    public fieldOfUseOptions: SelectOption[] = [];
    public sampleTypeOptions: SampleType[] = [];
    public sampleStateOptions: SelectOption[] = [];
    public sampleFormatOptions: { label: string; value: SampleFormat }[] = [
        { label: 'Standard', value: SampleFormat.STANDARD },
        { label: 'G-Class Dedicated', value: SampleFormat.G_CLASS },
        { label: 'Maybach Dedicated', value: SampleFormat.MAYBACH },
    ];
    public sampleBrandingOptions: { label: string; value: SampleBranding }[] = [
        { label: 'Standard', value: SampleBranding.STANDARD },
        { label: 'Manufaktur', value: SampleBranding.MANUFAKTUR },
        { label: 'Manufaktur Exclusive', value: SampleBranding.MANUFAKTUR_EXCLUSIVE },
    ];
    public languages: Language[] = [];
    public manufacturers: Manufacturer[] = [];

    // Category options
    public categoryOptions: SampleCategory[] = [];
    public categoryDisabled: boolean = true;

    // Details
    public details: Detail[] = [];
    public selectedDetails: Detail[] = [];
    public detailsDisabled: boolean = false;

    public media: Media | undefined = undefined;

    // Loading states
    public loading: boolean = false;
    public loadingLanguages: boolean = false;
    public loadingManufacturers: boolean = false;
    public firstLoad: boolean = true;

    constructor(
        private utilsService: UtilsService,
        private languageService: LanguageService,
        private sampleService: SampleService,
        private messageService: MessageService,
        private manufacturerService: ManufacturerService,
        private sampleTypeService: SampleTypeService,
        private sampleCategoryService: SampleCategoryService,
        private detailsService: DetailService,
        private mediaService: MediaService,
        private authService: AuthService
    ) {
    }

    public ngOnInit(): void {
        this.fieldOfUseOptions = this.utilsService.createDropdownOptions(FieldOfUse);
        this.sampleStateOptions = this.utilsService.createDropdownOptions(SampleState);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && changes['visible'].currentValue === true) {
            this.loadDetails();
            this.loadLanguages();
            this.loadManufacturers();
            this.loadSampleTypes();
            this.initializeFields();
        }
    }

    public canEditPrintedName(): boolean {
        const isProcessOwner = this.authService.hasRole(Role.SAMPLE_STUDIO_PROCESS_OWNER);
        const isMaintainer = this.authService.hasRole(Role.SAMPLE_STUDIO_MAINTAINER);
        return isProcessOwner || isMaintainer;
    }

    private initializeFields() {
        if (this.sample.typeId) {
            this.sampleCategoryService.getSampleCategoriesByType(this.sample.typeId).then((categories) => {
                this.categoryOptions = categories;
                // Automatically set category if there's only one allowed option
                if (this.categoryOptions.length === 1) {
                    this.sample.categoryId = this.categoryOptions[0].id;
                    this.categoryDisabled = true;
                } else this.categoryDisabled = this.categoryOptions.length == 0;
            }).catch(() => {
                this.messageService.add(
                    { severity: 'error', summary: 'Error', detail: 'Failed to fetch categories', life: 3000 });
            });
        }
        if (this.sample.mediaId) {
            this.mediaService.getMedia(this.sample.mediaId).then((media) => {
                this.media = media;
            }).catch((error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000,
                });
            });
        } else {
            this.media = undefined;
        }
    }

    public addLanguage() {
        this.sample.sampleLanguages.push(new SampleLanguageWriteDTO({}));
    }

    public removeLanguage(index: number) {
        this.sample.sampleLanguages.splice(index, 1);
    }

    public close() {
        this.visible = false;
        this.visibleChange.emit(this.visible);
    }

    public updateSample() {
        this.loading = true;
        this.sample.categoryDetailIds = this.selectedDetails.map((detail) => detail.id);
        if (this.media) {
            this.sample.mediaId = this.media.id;
        }
        this.sampleService
            .updateSample(this.sample)
            .then(() => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Sample updated',
                    life: 3000,
                });
                this.sampleUpdated.emit();
                this.close();
            })
            .catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 6000,
                });
            })
            .finally(() => {
                this.loading = false;
            });
    }

    public getAvailableLanguages(index: number): Language[] {
        const selectedLanguageIds = this.sample.sampleLanguages
            .filter((lang, idx) => idx !== index && lang.languageId != null)
            .map((lang) => lang.languageId);

        return this.languages.filter(
            (language) =>
                !selectedLanguageIds.includes(language.id) ||
                language.id === this.sample.sampleLanguages[index].languageId
        );
    }

    public loadSampleTypes() {
        this.sampleTypeService
            .getAllSampleTypes()
            .then((sampleTypes) => {
                this.sampleTypeOptions = sampleTypes;
            })
            .catch(() => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch sample types',
                    life: 3000,
                });
            });
    }

    public loadManufacturers() {
        this.loadingManufacturers = true;
        this.manufacturerService.getAllManufacturers().then((manufacturers) => {
            this.manufacturers = manufacturers;
        }).catch(() => {
            this.messageService.add(
                { severity: 'error', summary: 'Error', detail: 'Failed to fetch manufacturers', life: 3000 });
        }).finally(() => {
            this.loadingManufacturers = false;
        });
    }

    private loadLanguages() {
        this.loadingLanguages = true;
        this.languageService
            .getAllLanguages()
            .then((languages) => {
                this.languages = languages;
            })
            .catch(() => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to fetch languages',
                    life: 3000,
                });
            })
            .finally(() => {
                this.loadingLanguages = false;
            });
    }

    public loadDetails(): void {
        if (!this.sample.categoryId) {
            this.details = [];
            this.selectedDetails = [];
            this.detailsDisabled = true;
            return;
        }
        this.detailsService.getDetailsByCategory(this.sample.categoryId!).then((details) => {
            this.details = details;
            this.selectedDetails = this.details.filter((detail) => this.sample.categoryDetailIds.includes(detail.id));
            this.detailsDisabled = this.details.length === 0;
        }).catch(() => {
            this.messageService.add(
                { severity: 'error', summary: 'Error', detail: 'Failed to fetch details', life: 3000 });
        });
    }

    public onSampleTypeChange(sampleTypeId: number): void {
        if (sampleTypeId && !this.firstLoad) {
            // Reset category and load allowed categories for the selected type
            this.sample.categoryId = undefined;
            const selectedType = this.sampleTypeOptions.find((type) => type.id === sampleTypeId);
            if (selectedType?.defaultWeight) {
                this.sample.weight = selectedType.defaultWeight;
            }
            this.sampleCategoryService.getSampleCategoriesByType(sampleTypeId).then((categories) => {
                this.categoryOptions = categories;
                // Automatically set category if there's only one allowed option
                if (this.categoryOptions.length === 1) {
                    this.sample.categoryId = this.categoryOptions[0].id;
                    this.categoryDisabled = true;
                } else this.categoryDisabled = this.categoryOptions.length == 0;
            }).catch(() => {
                this.messageService.add(
                    { severity: 'error', summary: 'Error', detail: 'Failed to fetch categories', life: 3000 });
            });
        }
        this.firstLoad = false;
    }

    public onSampleCategoryChange(categoryId: number): void {
        if (categoryId) {
            const selectedCategory = this.categoryOptions.find((category) => category.id === categoryId);
            if (selectedCategory?.defaultWeight) {
                this.sample.weight = selectedCategory.defaultWeight;
            }
            this.loadDetails();
        }
    }

    protected readonly SampleState = SampleState;
}
